import { useEffect, useMemo, useState } from "react";
import { photoItems } from "@/mocks/homeData";
import SectionHeading from "./SectionHeading";

const cats = ["全部", "红毯", "现场", "街拍", "机场", "综艺", "音乐"];

export default function GallerySection() {
  const [cat, setCat] = useState("全部");
  const [active, setActive] = useState<number | null>(null);

  const list = useMemo(
    () => (cat === "全部" ? photoItems : photoItems.filter((p) => p.cat === cat)),
    [cat]
  );

  useEffect(() => {
    const onSearch = (e: Event) => {
      const kw = (e as CustomEvent).detail as string;
      const target = photoItems.find((p) => p.title.includes(kw) || p.cat.includes(kw));
      if (target) {
        setCat("全部");
        setActive(target.id);
      } else {
        window.dispatchEvent(new CustomEvent("chigua:toast", { detail: `未找到与「${kw}」相关的现场生图，试试红毯、街拍、现场等关键词` }));
      }
    };
    window.addEventListener("chigua:search", onSearch);
    return () => window.removeEventListener("chigua:search", onSearch);
  }, []);

  return (
    <section id="gallery" className="w-full bg-background-50 py-14 md:py-20 scroll-mt-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
          <SectionHeading
            kicker="高清生图"
            title="高清现场生图流"
            desc="无滤镜直出的高清抓拍，不同机位同框放送。点开任意一张即可放大查看细节。"
          />
          <div className="flex flex-wrap gap-2 lg:justify-end">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => {
                  setCat(c);
                  setActive(null);
                }}
                className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition whitespace-nowrap cursor-pointer ${
                  cat === c
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-9 columns-2 lg:columns-3 gap-4 md:gap-5 space-y-4 md:space-y-5">
          {list.map((p) => (
            <button
              key={p.id}
              onClick={() => setActive(p.id)}
              className="group block w-full break-inside-avoid overflow-hidden rounded-lg bg-background-100 border border-background-200 cursor-pointer text-left"
            >
              <div className="relative w-full overflow-hidden">
                <img
                  src={p.img}
                  alt={`${p.place}${p.title}高清现场生图`}
                  className={`w-full object-cover object-top group-hover:scale-105 transition-transform duration-500 ${
                    p.width === "tall" ? "aspect-[3/4]" : "aspect-[4/3]"
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition"></div>
                <span className="absolute top-3 left-3 px-2 py-0.5 rounded text-[10px] font-semibold text-background-50 bg-foreground-950/60 whitespace-nowrap opacity-0 group-hover:opacity-100 transition">
                  {p.place}
                </span>
                <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] text-background-100 bg-foreground-950/55 whitespace-nowrap">
                  <i className="ri-heart-3-line text-accent-400"></i> {p.likes.toLocaleString()}
                </span>
              </div>
              <div className="p-3 md:p-4">
                <h3 className="text-sm md:text-base font-semibold text-foreground-950 leading-snug">{p.title}</h3>
                <p className="mt-1 text-xs text-foreground-500">
                  <i className="ri-price-tag-3-line mr-1"></i>
                  {p.cat}图集 · {p.place}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {active !== null &&
        (() => {
          const item = photoItems.find((x) => x.id === active);
          if (!item) return null;
          return (
            <div
              className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-foreground-950/85 backdrop-blur-sm"
              onClick={() => setActive(null)}
            >
              <button
                aria-label="关闭"
                onClick={() => setActive(null)}
                className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/10 text-background-50 hover:bg-primary-500 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
              <figure className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
                <div className="relative overflow-hidden rounded-lg aspect-[4/3]">
                  <img src={item.img} alt={`${item.title}高清大图预览`} className="w-full h-full object-cover" />
                </div>
                <figcaption className="mt-4 text-center text-background-100">
                  <p className="text-base md:text-lg font-semibold text-background-50">{item.title}</p>
                  <p className="mt-1 text-sm text-background-100/75">
                    {item.place} · {item.cat}高清图集 · 已收录至相关事件页
                  </p>
                </figcaption>
              </figure>
            </div>
          );
        })()}
    </section>
  );
}